/**
 * Onboarding and paywall funnel events (v1.5.0, docs/v150-spec.md §2.9, §3.1).
 *
 * `track(name, at?)` queues `{ name, at }` (epoch ms) in localStorage and
 * `flushFunnel()` posts the queue to POST /subscription/events once a cached
 * idToken exists, so events from the signed-out onboarding screens wait for
 * the sign-up and are then recorded against the new account. The server keeps
 * the FIRST time it sees each name (`if_not_exists` on PROFILE), so the queue
 * keeps the first occurrence of a name too, and re-sending one later is
 * harmless.
 *
 * Background work the user never asked for: silentFetch only (no toast), and
 * nothing here may ever throw into a caller — a funnel failure must never
 * break the screen that recorded it. Sign-out and account deletion call
 * clearFunnelQueue so one person's queued events never reach the next account
 * signed in on the device.
 */

import { silentFetch } from '../api/client';
import { getCachedIdToken, isOffline } from '../../services/network';

export const FUNNEL_QUEUE_KEY = 'bt_funnel_queue';
export const FUNNEL_QUEUE_MAX = 50;
// POST /subscription/events accepts at most 20 events per request.
const BATCH_SIZE = 20;

function readQueue() {
  try {
    const parsed = JSON.parse(localStorage.getItem(FUNNEL_QUEUE_KEY) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter((e) => e && typeof e.name === 'string' && Number.isFinite(e.at))
      : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    if (queue.length === 0) localStorage.removeItem(FUNNEL_QUEUE_KEY);
    else localStorage.setItem(FUNNEL_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* storage unavailable — the event is simply not recorded */
  }
}

function toEpochMs(at) {
  if (at instanceof Date) return at.getTime();
  if (typeof at === 'number') return at;
  if (typeof at === 'string' && at) return Date.parse(at);
  return NaN;
}

/**
 * Queue a funnel event and try to send it. `at` (Date, epoch ms or ISO
 * string) defaults to now. Returns nothing and never throws.
 */
export function track(name, at) {
  try {
    if (typeof name !== 'string' || !name) return;
    const ms = toEpochMs(at);
    const queue = readQueue();
    if (!queue.some((e) => e.name === name) && queue.length < FUNNEL_QUEUE_MAX) {
      queue.push({ name, at: Number.isFinite(ms) ? ms : Date.now() });
      writeQueue(queue);
    }
  } catch {
    /* never throw into a caller */
  }
  flushFunnel().catch(() => {});
}

let flushing = null; // the running flush, shared by concurrent callers
let flushAgain = false; // something was queued while a flush was running

const entryKey = (e) => `${e.name}|${e.at}`;

async function runFlush() {
  if (!getCachedIdToken() || isOffline()) return;
  // Bounded rounds: a full queue is three batches, and a storage write that
  // silently fails can never turn this into an endless POST loop.
  for (let round = 0; round < Math.ceil(FUNNEL_QUEUE_MAX / BATCH_SIZE); round += 1) {
    const batch = readQueue().slice(0, BATCH_SIZE);
    if (batch.length === 0) return;
    const res = await silentFetch('/subscription/events', 'POST', {
      events: batch.map(({ name, at }) => ({ name, at })),
    });
    if (!res) return; // network, 401 or 5xx — keep everything for the next flush
    // Drop exactly what was accepted; anything queued meanwhile stays.
    const sent = new Set(batch.map(entryKey));
    writeQueue(readQueue().filter((e) => !sent.has(entryKey(e))));
  }
}

/**
 * Post every queued event (in batches of 20) and clear what the server
 * accepted. A no-op while signed out or offline. Resolves, never rejects.
 */
export function flushFunnel() {
  if (flushing) {
    flushAgain = true;
    return flushing;
  }
  flushing = runFlush()
    .catch(() => {})
    .finally(() => {
      flushing = null;
      if (flushAgain) {
        flushAgain = false;
        flushFunnel();
      }
    });
  return flushing;
}

/** Forget every queued event (sign-out and account deletion). */
export function clearFunnelQueue() {
  try {
    localStorage.removeItem(FUNNEL_QUEUE_KEY);
  } catch {
    /* nothing to clear */
  }
}
